import React from 'react';
import { CustomerForm } from './CustomerForm';
import { PortalAccessSection } from './PortalAccessSection';
import type { Customer } from '../../types/domain';
import type { CreateCustomerInput, UpdateCustomerInput } from '../../types/api';

interface CustomerModalProps {
  isOpen: boolean;
  customer?: Customer;
  onClose: () => void;
  onSubmit: (data: CreateCustomerInput | UpdateCustomerInput) => void;
  isSubmitting?: boolean;
}

export const CustomerModal: React.FC<CustomerModalProps> = ({
  isOpen,
  customer,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  console.log('[CustomerModal] Rendering with props:', { isOpen, customer, isSubmitting });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const handleFormSubmit = async (data: CreateCustomerInput | UpdateCustomerInput) => {
    try {
      await onSubmit(data);
    } catch (error) {
      console.error('[CustomerModal] Error submitting form:', error);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
      onSubmit={(e) => e.stopPropagation()}
    >
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div 
          className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
              {customer ? 'Edit Customer' : 'Add Customer'}
            </h3>

            <CustomerForm
              customer={customer}
              onSubmit={handleFormSubmit}
              onCancel={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                onClose();
              }}
              isSubmitting={isSubmitting}
            />

            {/* Portal Access Section - only show when editing existing customer */}
            {customer && (
              <PortalAccessSection
                customerId={customer.customerId}
                customerEmail={customer.email}
                customerName={customer.contactName}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
